import { FieldKey } from '../../context/field-keys.js';
import { divideBy12, multiplyBy12 } from '../../shared/conversion.js';
import { divide, multiply } from '../../shared/math.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Availability service-charge trio (T2.3), consolidated from
 * `projects/miq/auto-calculation/src/lib/listeners/availability-rates.binder.ts`
 * (`serviceChargeListener`, host `addListenerToServiceCharge`) and its wiring in
 * `src/app/records/controls/common/listeners/availability/availability-auto-calc-listeners.ts`.
 *
 *   per-area (PA) = cam_charges_per_area_value      → {@link FieldKey.AvailCamChargesPerArea} (col 18598)
 *   monthly  (M)  = cam_charges                     → {@link FieldKey.AvailCamCharges} (col 17008)
 *   yearly   (Y)  = service_charge_amount_yearly    → {@link FieldKey.AvailServiceChargeYearly} (col 87)
 *
 *   source PA: M = PA×S       Y = 12×(PA×S)
 *   source M : PA = M÷S       Y = 12×M
 *   source Y : M = Y÷12       PA = (Y÷12)÷S
 *   source S : composite recompute (see {@link serviceChargeFromSpaceSizeRule})
 *
 * Same faithful semantics as the asking-rent matrix: unrounded intermediates, each output
 * rounded independently, guards mean NO WRITE, `''` sources ignored. Deliberate normalization
 * (CHANGELOG): the legacy Y-from-PA branch wrote a bogus `0` when the space size was empty
 * (locally-initialised `serviceChargeMonthly = 0` × 12); these rules guard on size instead.
 */
const num = v => v == null || v === '' ? null : Number(v);
/** Mirrors the legacy `updateValue`: round, and write only finite results (never null/NaN/∞). */
function setIfFinite(ctx, key, raw, round) {
  if (!Number.isFinite(raw)) {
    return;
  }
  const rounded = round(raw, key);
  if (!Number.isFinite(rounded)) {
    return;
  }
  ctx.set(key, rounded);
}
/** M = PA × S (binder `case ServiceCharge`, ServiceChargePerMonth branch). */
export const serviceChargeMonthlyFromPerAreaRule = {
  id: 'availability.service-charge.monthly-from-per-area',
  inputs: [FieldKey.AvailCamChargesPerArea, FieldKey.AvailSpaceSize],
  outputs: [FieldKey.AvailCamCharges],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const perArea = num(ctx.get(FieldKey.AvailCamChargesPerArea));
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (perArea == null || !size) {
      return;
    }
    setIfFinite(ctx, FieldKey.AvailCamCharges, multiply(perArea, size), round);
  }
};
/** Y = 12 × (PA × S) (same case, ServiceChargePerYear branch; size-guard normalized). */
export const serviceChargeYearlyFromPerAreaRule = {
  id: 'availability.service-charge.yearly-from-per-area',
  inputs: [FieldKey.AvailCamChargesPerArea, FieldKey.AvailSpaceSize],
  outputs: [FieldKey.AvailServiceChargeYearly],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const perArea = num(ctx.get(FieldKey.AvailCamChargesPerArea));
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (perArea == null || !size) {
      return;
    }
    setIfFinite(ctx, FieldKey.AvailServiceChargeYearly, multiplyBy12(multiply(perArea, size)), round);
  }
};
/** PA = M ÷ S (binder `case ServiceChargePerMonth`, ServiceCharge branch). */
export const serviceChargePerAreaFromMonthlyRule = {
  id: 'availability.service-charge.per-area-from-monthly',
  inputs: [FieldKey.AvailCamCharges, FieldKey.AvailSpaceSize],
  outputs: [FieldKey.AvailCamChargesPerArea],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const monthly = num(ctx.get(FieldKey.AvailCamCharges));
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (monthly == null || !size) {
      return;
    }
    setIfFinite(ctx, FieldKey.AvailCamChargesPerArea, divide(monthly, size), round);
  }
};
/** Y = 12 × M (same case, ServiceChargePerYear branch — unconditional on size). */
export const serviceChargeYearlyFromMonthlyRule = {
  id: 'availability.service-charge.yearly-from-monthly',
  inputs: [FieldKey.AvailCamCharges],
  outputs: [FieldKey.AvailServiceChargeYearly],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const monthly = num(ctx.get(FieldKey.AvailCamCharges));
    if (monthly == null) {
      return;
    }
    setIfFinite(ctx, FieldKey.AvailServiceChargeYearly, multiplyBy12(monthly), round);
  }
};
/** M = Y ÷ 12 (binder `case ServiceChargePerYear`, ServiceChargePerMonth branch). */
export const serviceChargeMonthlyFromYearlyRule = {
  id: 'availability.service-charge.monthly-from-yearly',
  inputs: [FieldKey.AvailServiceChargeYearly],
  outputs: [FieldKey.AvailCamCharges],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const yearly = num(ctx.get(FieldKey.AvailServiceChargeYearly));
    if (yearly == null) {
      return;
    }
    setIfFinite(ctx, FieldKey.AvailCamCharges, divideBy12(yearly), round);
  }
};
/** PA = (Y ÷ 12) ÷ S (same case, ServiceCharge branch; exact legacy op chain, unrounded monthly). */
export const serviceChargePerAreaFromYearlyRule = {
  id: 'availability.service-charge.per-area-from-yearly',
  inputs: [FieldKey.AvailServiceChargeYearly, FieldKey.AvailSpaceSize],
  outputs: [FieldKey.AvailCamChargesPerArea],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const yearly = num(ctx.get(FieldKey.AvailServiceChargeYearly));
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (yearly == null || !size) {
      return;
    }
    setIfFinite(ctx, FieldKey.AvailCamChargesPerArea, divide(divideBy12(yearly), size), round);
  }
};
/**
 * Space-size-driven service-charge recompute (binder `case SpaceSize` of `serviceChargeListener`,
 * host `spaceSizeServiceCharge$` wiring: ctrlTwo = per-area rate). One composite rule for the same
 * reason as the asking-rent space-size rule: the fallback branches couple multiple outputs to
 * which driver value was present.
 *
 *   S must be > 0 (legacy `spaceSize <= 0 → break` — nothing fires, fallbacks included).
 *   PA present:  M = S×PA, Y = 12×(S×PA).
 *   PA empty:
 *     `deps.enableChainedSizeFallbacks` off (default, MIQ 1.0): nothing — legacy matrix.
 *     on (MIQ 2.0): totals are user-entered facts and stay authoritative —
 *       M present: PA = M÷S; Y = 12×M only when Y is empty (a populated Y is preserved).
 *       else Y present: M = Y÷12, PA = (Y÷12)÷S (unrounded derived monthly feeds PA).
 */
export const serviceChargeFromSpaceSizeRule = {
  id: 'availability.service-charge.from-space-size',
  inputs: [FieldKey.AvailSpaceSize, FieldKey.AvailCamChargesPerArea, FieldKey.AvailCamCharges, FieldKey.AvailServiceChargeYearly],
  outputs: [FieldKey.AvailCamChargesPerArea, FieldKey.AvailCamCharges, FieldKey.AvailServiceChargeYearly],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (size == null || Number.isNaN(size) || size <= 0) {
      return;
    }
    const perArea = num(ctx.get(FieldKey.AvailCamChargesPerArea));
    if (perArea != null && !Number.isNaN(perArea)) {
      const monthly = multiply(size, perArea);
      setIfFinite(ctx, FieldKey.AvailCamCharges, monthly, round);
      setIfFinite(ctx, FieldKey.AvailServiceChargeYearly, multiplyBy12(monthly), round);
      return;
    }
    if (!deps?.enableChainedSizeFallbacks) {
      return; // MIQ 1.0: legacy matrix — per-area-driven records only
    }
    const monthlyTotal = num(ctx.get(FieldKey.AvailCamCharges));
    const yearlyTotal = num(ctx.get(FieldKey.AvailServiceChargeYearly));
    if (monthlyTotal != null && !Number.isNaN(monthlyTotal)) {
      setIfFinite(ctx, FieldKey.AvailCamChargesPerArea, divide(monthlyTotal, size), round);
      if (yearlyTotal == null || Number.isNaN(yearlyTotal)) {
        setIfFinite(ctx, FieldKey.AvailServiceChargeYearly, multiplyBy12(monthlyTotal), round);
      }
    } else if (yearlyTotal != null && !Number.isNaN(yearlyTotal)) {
      const derivedMonthly = divideBy12(yearlyTotal);
      setIfFinite(ctx, FieldKey.AvailCamCharges, derivedMonthly, round);
      setIfFinite(ctx, FieldKey.AvailCamChargesPerArea, divide(derivedMonthly, size), round);
    }
  }
};
export const availabilityServiceChargeRules = [serviceChargeMonthlyFromPerAreaRule, serviceChargeYearlyFromPerAreaRule, serviceChargePerAreaFromMonthlyRule, serviceChargeYearlyFromMonthlyRule, serviceChargeMonthlyFromYearlyRule, serviceChargePerAreaFromYearlyRule, serviceChargeFromSpaceSizeRule];
